import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectSelectedDatabase, selectSelectedStudy } from '../../common/databaseSlice';
import {
    useGetSessionsQuery,
    useGetVisitByImagingQuery,
    useGetVisitByInstrumentQuery,
} from '../../../api/dashboardApiSlice';
import BarChart from './BarChart';
import ChartPaper from './ChartPaper';
import { Virtuoso } from 'react-virtuoso';
import LoadingDashlet from "../../../components/loaders/LoadingDashlet";
import { useOutletContext } from "react-router-dom";
import { useGetDatabaseVersionQuery } from "../../../api/databaseApiSlice";
import { SalesforceIssueRecord, useGetSalesforceReleaseQuery } from "../../../api/issueApiSlice";
import { MainDomainEnum } from "../dashboardTypes";

const ResearchDomains: React.FC = () => {
    const database = useSelector(selectSelectedDatabase);
    const selectedStudy = useSelector(selectSelectedStudy);

    const { data: version } = useGetDatabaseVersionQuery({ database });
    const { data: salesforceReleaseIssues } = useGetSalesforceReleaseQuery({ studyName: selectedStudy, version: version  });

    const salesforceRecordsByInstrument = useMemo(() => {
        if (!salesforceReleaseIssues?.records) return {};
        
        const recordMap: Record<string, SalesforceIssueRecord[]> = {};
        
        for (const record of salesforceReleaseIssues.records) {
            const instrument = record.instrument;
            if (!recordMap[instrument]) {
                recordMap[instrument] = [];
            }
            recordMap[instrument].push(record);
        }
        
        return recordMap;
    }, [salesforceReleaseIssues]);

    const { data: constructs, isFetching: isFetchingConstructs } = useGetVisitByInstrumentQuery({ database: database })
    const { data: imaging, isFetching: isFetchingImaging } = useGetVisitByImagingQuery({ database: database })
    const { data: sessions, isFetching: isFetchingSessions } = useGetSessionsQuery({ database });

    const {
        view,
        selectedDomain,
        instrumentFilter,
        selectedSessionFilter
    } = useOutletContext<any>();

    const sessionInfoMap = useMemo(() => {
        if (!sessions) return {};
        const data: any = {}
        for (const session of sessions) {
            data[session.label] = session;
        }
        return data;
    }, [sessions]);

    const instrumentDomainRelation = useMemo(() => {
        if (isFetchingImaging || isFetchingConstructs) return {};
        const domainInstrumentMap: Record<string, string> = {};

        Object.keys(imaging).forEach((imagingInstrument) => {
            domainInstrumentMap[imagingInstrument] = MainDomainEnum.IMAGING;
        })

        const domains = Object.keys(constructs)
        domains.forEach(domain => {
            const instrumentKeys = Object.keys(constructs[domain]);
            instrumentKeys.forEach(instrument => {
                domainInstrumentMap[instrument] = domain;
            });
        })
        return domainInstrumentMap;
    }, [constructs, imaging]);

    const renderChart = (chart: any, index: number) => {
        if (chart?.static_instrument) {
            return (
                <ChartPaper
                    key={index}
                    title={chart?.title}
                    table_name={chart?.table_name}
                    ddAvailable={true}
                    imagingData={imaging}
                    eegData={constructs?.["Tabular EEG"]}
                    salesforceRecordsByInstrument={salesforceRecordsByInstrument}
                    instrumentDomainRelation={instrumentDomainRelation}
                >
                    {null}
                </ChartPaper>
            )
        }
        return (
            <BarChart
                key={index}
                data={chart?.data}
                source={chart?.source}
                title={chart?.title}
                selectedSessionFilter={selectedDomain === "cohort-demographics-explorer" ? undefined : selectedSessionFilter}
                ddAvailable={true}
                imagingData={imaging}
                eegData={constructs?.["Tabular EEG"]}
                sessionInfoMap={sessionInfoMap}
                table_name={chart?.table_name}
                showAsTable={view === "table"}
                salesforceRecordsByInstrument={salesforceRecordsByInstrument}
                instrumentDomainRelation={instrumentDomainRelation}
            />
        )
    }

    const getList = () => {
        if (instrumentFilter.length > 0) {
            const regex = new RegExp(instrumentFilter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
            let dashletData = imaging;
            Object.keys(constructs).forEach((key) => {
                dashletData = {
                    ...dashletData,
                    ...constructs[key]
                }
            })

            return Object.keys(dashletData || {})
                .filter((key: string) => regex.test(dashletData[key]?.title?.toLowerCase()))
                .map((key) => {
                    return {
                        table_name: key,
                        ...dashletData[key]
                    }
                });

        }
        let dashletData: any
        switch (selectedDomain) {
        case MainDomainEnum.IMAGING:
            dashletData = imaging;
            break;
        case MainDomainEnum.ALL_DOMAINS:
            dashletData = imaging;
            Object.keys(constructs).forEach((key) => {
                dashletData = {
                    ...dashletData,
                    ...constructs[key]
                }
            })
            break;
        default:
            dashletData = constructs[selectedDomain];
            break;
        }
        return Object.keys(dashletData || {}).map((key) => {
            return {
                table_name: key,
                ...dashletData[key]
            }
        })
    }

    if (isFetchingImaging || isFetchingConstructs || isFetchingSessions) {
        return <LoadingDashlet height={'calc(33% - 16px)'}/>
    }

    return (
        <Virtuoso
            style={{ height: '100%' }}
            data={getList()}
            itemContent={(index, user) => (
                renderChart(user, index)
            )}
        />
    )
}

export default ResearchDomains;
